/**
 * PlanBadge — tiny current-plan + renewal indicator for the sidebar footer.
 *
 * Shows the org's plan and a countdown (trial ends / renews) or a "payment due"
 * state during dunning; clicking opens Settings → Plan & Billing. Reads
 * useEntitlements (cached). Collapses to a single icon when the sidebar is.
 *
 * @since 1.0.0
 */
import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useEntitlements } from '@/shared/hooks/useEntitlements';
import { displayPlanName } from '@/shared/lib/entitlements';

export function PlanBadge({ onNavigated }: { onNavigated?: () => void }) {
    const navigate = useNavigate();
    const { plan, phase, entitlements, loading, previewPhase } = useEntitlements();
    // Capture "now" once (lazy init) so the render stays pure.
    const [nowMs] = useState(() => Date.now());

    const periodEnd = entitlements?.currentPeriodEnd ?? null;
    const days = useMemo(() => {
        if (!periodEnd) return null;
        const t = Date.parse(periodEnd);
        return Number.isNaN(t) ? null : Math.ceil((t - nowMs) / 86_400_000);
    }, [periodEnd, nowMs]);

    if (loading || !plan) return null;

    const name = displayPlanName(plan);
    const isPaid = !!entitlements && entitlements.priceMonthlyInr != null && entitlements.priceMonthlyInr > 0 && plan !== 'Free Forever';
    const dunning = phase !== 'active';          // grace / warning / readonly / locked
    // Show a countdown for any near-future period end (< 400d filters the
    // legacy 100-year "Free Forever" sentinel); label by paid vs trial.
    const showCountdown = !dunning && days != null && days >= 0 && days < 400;

    const sub = dunning
        ? (previewPhase ? `Preview: ${previewPhase}` : 'Payment due — tap to fix')
        : showCountdown
            ? (isPaid ? `Renews in ${days}d` : `Trial ends in ${days}d`)
            : 'Tap to manage plan';

    const go = () => { navigate({ to: '/settings', hash: 'billing' }); onNavigated?.(); };

    return (
        <button
            onClick={go}
            title="Manage plan & billing"
            className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors group"
        >
            <div className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dunning ? 'bg-amber-500' : 'bg-teal-500'}`} />
                <span className="text-[11px] font-medium text-stone-500 dark:text-stone-400 truncate group-hover:text-stone-700 dark:group-hover:text-stone-200">
                    {name}
                </span>
            </div>
            <p className="text-[10px] text-stone-400 ml-3 mt-0.5">{sub}</p>
        </button>
    );
}
